<template>
  <main class="container">
    <table class="table table-success table-striped">
      <thead>
      <tr>
        <th>OPTIONS</th>
        <th>LINK</th>
        <th>SHORT LINK</th>
        <th>QR CODE</th>
      </tr>
      </thead>
      <tbody>
      <!-- Show only paginated links -->
      <tr v-for="dataLink in paginatedLinks" :key="dataLink.id_links">
        <td class="d-flex flex-column flex-lg-row justify-content-evenly align-items-center flex-strow">
          <button class="btn btn-primary option-button" @click="editLink(dataLink.id_links, dataLink.original_link)">
            Change link
          </button>
          <button class="btn btn-danger option-button" @click="confirmAndDeleteLink(dataLink.id_links)">
            Delete link
          </button>
        </td>
        <td>
          <span
              class="align-middle"
              tabindex="0"
              role="button"
              :title="dataLink.original_link"
              @click="showFullUrl(dataLink.original_link)"
              style="cursor: pointer"
          >
            {{ truncateUrl(dataLink.original_link) }}
          </span>
        </td>
        <td class="align-middle">
          <span
              class="align-middle"
              tabindex="0"
              role="button"
              :title="formatShortLink(dataLink.short_link)"
              @click="showFullUrl(formatShortLink(dataLink.short_link))"
              style="cursor: pointer"
          >
            {{ formatShortLink(dataLink.short_link) }}
          </span>
        </td>
        <td class="align-middle">
          <img v-if="dataLink.qrCode" :src="dataLink.qrCode" alt="QR Code" @click="showQRCodeModal(dataLink)"/>
        </td>
      </tr>
      </tbody>
    </table>

    <!-- Bootstrap 5 pagination controls -->
    <nav v-if="totalPages > 1" aria-label="Page navigation">
      <ul class="pagination justify-content-center">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <button class="page-link" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">Previous</button>
        </li>
        <li
            v-for="page in totalPages"
            :key="page"
            class="page-item"
            :class="{ active: currentPage === page }"
        >
          <button class="page-link" @click="goToPage(page)">{{ page }}</button>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <button class="page-link" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">Next
          </button>
        </li>
      </ul>
    </nav>
  </main>
</template>

<script>
import axios from "axios";
import QRCode from "qrcode";

import {
  confirmAndDeleteLinkService,
  editLink,
  formatShortLinkService,
  showQRCodeModal,
  truncateUrl
} from "@/services/links/linksComponentService";

const apiUrl = process.env.VUE_APP_API_URL;

export default {
  name: "LinksTable",
  /**
   * Provides default data structure for component state.
   *
   * @return {Object} Returns an object containing the default state, including:
   * - `dataLinks`: An empty array for storing data links.
   * - `currentPage`: A number representing the current page, default is 1.
   * - `pageSize`: A number indicating the number of items per page, default is 5.
   */
  data() {
    return {
      dataLinks: [],
      currentPage: 1, // Current page number
      pageSize:5,   // Items per page
    };
  },
  /**
   * A set of computed properties for handling pagination logic.
   * These properties calculate the paginated links and the total number of pages
   * based on the current state of the data.
   *
   * @property {Function} paginatedLinks - Computes and returns a subset of links
   *                                       for the current page based on
   *                                       currentPage and pageSize.
   * @property {Function} totalPages - Computes and returns the total number of
   *                                   pages based on the number of links available
   *                                   and the page size.
   */
  computed: {
    // Returns the links for the current page
    /**
     * Retrieves a subset of links from the dataLinks array corresponding to the current page.
     *
     * This method calculates the starting index based on the current page and page size,
     * and slices the dataLinks array to include only the items for the specific page.
     *
     * @return {Array} A subset of links for the current page.
     */
    paginatedLinks() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.dataLinks.slice(start, start + this.pageSize);
    },
    // Calculates the total number of pages
    /**
     * Calculates the total number of pages based on the total number of items
     * and the number of items per page.
     *
     * @return {number} The total number of pages.
     */
    totalPages() {
      return Math.ceil(this.dataLinks.length / this.pageSize);
    }
  },
  /**
   * Fetches data from a given API endpoint using the authentication token
   * and ID stored in sessionStorage. Populates the component's `dataLinks` property
   * with processed links data, creating QR codes for each link.
   * Handles errors that occur during the data fetching process or when authentication details are missing.
   *
   * @return {Promise<void>} Resolves when the data fetching and QR code generation processes are complete. Logs errors if any occur.
   */
  async mounted() {
    const id = sessionStorage.getItem('id');
    const token = sessionStorage.getItem('token');

    if (id && token) {
      try {
        const response = await axios.get(`${apiUrl}/links`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const data = response.data;
        this.dataLinks = await Promise.all(
            data.links.map(async link => ({
              ...link,
              qrCode: await QRCode.toDataURL(formatShortLinkService(link.short_link))
            }))
        );
      } catch (error) {
        console.error('Error getting data:', error);
      }
    } else {
      console.error('Id or token was not found in sessionStorage');
    }
  },
  /**
   * Contains various methods for handling link-related functionalities
   * and navigating between pages in a paginated system.
   */
  methods: {
    /**
     * Service function used to format a given URL or link into a shortened version.
     * Typically used to make longer URLs more user-friendly and easier to share.
     *
     * This function delegates the functionality to the `formatShortLinkService`,
     * which performs the actual shortening process.
     */
    formatShortLink: formatShortLinkService,
    /**
     * Truncates a given URL to a specified maximum length by replacing the middle part with ellipses (...).
     *
     * This function ensures that the starting and ending parts of the URL are preserved while reducing
     * the excess length in the middle when the URL exceeds the maximum length.
     *
     * @param {string} url - The URL to be truncated.
     * @param {number} maxLength - The maximum allowed length of the URL. Must be greater than 5.
     * @returns {string} - The truncated URL if the original URL exceeds the maximum length, or the original URL if it is within the limit.
     * @throws {Error} - Throws an error if the maxLength parameter is less than or equal to 5.
     */
    truncateUrl,
    /**
     * Edits an existing link by updating its URL.
     *
     * @param {string} link - The identifier or reference of the link to be edited.
     * @param {string} url - The new URL to update the specified link with.
     * @return {void} No return value.
     */
    editLink(link, url) {
      editLink(this, link, url);
    },
    /**
     * Confirms and deletes a link based on the provided identifier.
     *
     * @param {string} id - The unique identifier of the link to be deleted.
     * @return {Promise<void>} A promise that resolves when the link has been successfully deleted.
     */
    async confirmAndDeleteLink(id) {
      await confirmAndDeleteLinkService(this, id);
    },
    /**
     * Displays a QR code modal with the provided data link.
     *
     * @param {string} dataLink - The URL or data to be embedded in the QR code.
     * @return {Promise<void>} Resolves once the QR code modal is displayed.
     */
    async showQRCodeModal(dataLink) {
      await showQRCodeModal(this, dataLink);
    },
    /**
     * Navigates to a specified page if the page number is within the valid range.
     *
     * @param {number} page - The page number to navigate to. Must be between 1 and the total number of pages.
     * @return {void} No return value.
     */
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    /**
     * Displays a modal showing the full URL as a clickable link and provides an option to copy the URL to the clipboard.
     *
     * @param {string} url The URL to be displayed and copied.
     * @return {Promise<void>} A promise that resolves once the URL is copied to the clipboard, or rejects if there is an error during copying.
     */
    showFullUrl(url) {
      this.$swal.fire({
        title: "URL",
        html: `<a href="${url}" target="_blank" rel="noopener">${url}</a>`,
        confirmButtonText: "Copy and close",
        preConfirm: () => {
          return navigator.clipboard.writeText(url);
        }
      });
    }
  }
};
</script>
